import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./AutoScrollImageList.css"; // External CSS
import { ThemeContext } from "../Contexts/themeProvider";
import LazyScrollImage from "./LazyScrollImage";

const SCROLL_SPEED_PX_PER_FRAME = 2.5;

export default function AutoScrollImageList({images}) {
  const theme = useContext(ThemeContext);
  const darkMode = theme.state.darkMode; 
  const [selectedImage, setSelectedImage] = useState(null);
  const scrollerRef = useRef(null);
  const directionRef = useRef(1); // 1 = down, -1 = up
  const [readyToScroll, setReadyToScroll] = useState(false);
  const eagerCount = 6;
  const eagerDoneRef = useRef(new Set());

  const visibleImages = useMemo(() => {
    return (images || []).filter((img) => img && !img.isDeleted && img.link);
  }, [images]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let raf = 0;
    const tick = () => {
      const node = scrollerRef.current;
      if (!node) return;

      if (readyToScroll) {
        const maxScrollTop = Math.max(0, node.scrollHeight - node.clientHeight);
        if (maxScrollTop > 0) {
          const next =
            node.scrollTop + directionRef.current * SCROLL_SPEED_PX_PER_FRAME;

          // Bounce at ends so content is always reachable by scrolling back.
          if (next >= maxScrollTop) {
            directionRef.current = -1;
            node.scrollTop = maxScrollTop;
          } else if (next <= 0) {
            directionRef.current = 1;
            node.scrollTop = 0;
          } else {
            node.scrollTop = next;
          }
        }
      }

      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [readyToScroll]);

  useEffect(() => {
    // Reset readiness when images change.
    eagerDoneRef.current = new Set();
    setReadyToScroll(false);
  }, [visibleImages]);

  const markEagerDone = (id) => {
    if (readyToScroll) return;
    eagerDoneRef.current.add(id);
    const need = Math.min(eagerCount, visibleImages.length);
    if (need === 0) {
      setReadyToScroll(true);
      return;
    }
    if (eagerDoneRef.current.size >= need) {
      setReadyToScroll(true);
    }
  };

  return (
    <div className={`scroll-wrapper mt-5 ${!darkMode ? "dark" :"white"}`}>
      {/* Modal */}
      {selectedImage && (
        <div className="modal-backdrop" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="Enlarged" className="modal-image" />
        </div>
      )}

      {/* Scrollable image grid (auto-scroll that pauses on interaction) */}
      <div className="scroll-scroller mt-2" ref={scrollerRef}>
        <div className="scroll-container">
          {visibleImages.map((img, index) => (
            <LazyScrollImage
              key={`img-${img.link}-${index}`}
              scrollerRef={scrollerRef}
              id={`img-${img.link}-${index}`}
              src={img.link}
              alt={`Image ${index + 1}`}
              className="scroll-image"
              frameClass="scroll-imageFrame"
              pendingClass="scroll-imageFrame--pending"
              fallbackClass="scroll-imageFallback"
              onEagerDone={markEagerDone}
              countEagerErrorTowardReady={false}
              eager={index < eagerCount}
              onClick={() => setSelectedImage(img.link)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
